import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Sync,
  CloudSync,
  CheckCircle,
  Error,
  Warning,
  Schedule,
  Settings,
  ExpandMore,
  PlayArrow,
  GetApp,
  Assessment
} from '@mui/icons-material';
import axios from 'axios';
import { getToken, base_url } from '../../config/config';

const SyncManagement = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [autoSyncDialogOpen, setAutoSyncDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [alert, setAlert] = useState(null);
  const [autoSyncSettings, setAutoSyncSettings] = useState({
    enabled: false,
    intervalMinutes: 60
  });
  const [importSettings, setImportSettings] = useState({
    entityType: 'products',
    filters: {}
  });

  useEffect(() => {
    fetchSyncStatus();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${base_url}/v1/stock/sync/status`, { headers });
      setSyncStatus(response.data.data);
      
      if (response.data.data.activeDataSource) {
        setAutoSyncSettings({
          enabled: response.data.data.activeDataSource.autoSync,
          intervalMinutes: response.data.data.activeDataSource.syncInterval
        });
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
      setAlert({ severity: 'error', message: 'Failed to fetch sync status' });
    } finally {
      setLoading(false);
    }
  };

  const handleManualSync = async (options = {}) => {
    try {
      setSyncing(true);
      setSyncResults(null);
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${base_url}/v1/stock/sync/all`, {
        syncProducts: options.syncProducts !== false,
        syncOwners: options.syncOwners !== false,
        syncMoves: options.syncMoves !== false
      }, { headers });
      
      setSyncResults(response.data.data);
      setAlert({ 
        severity: 'success', 
        message: `Sync completed: ${response.data.data.summary.totalSuccessful} successful, ${response.data.data.summary.totalFailed} failed` 
      });
      
      await fetchSyncStatus();
    } catch (error) {
      console.error('Error during sync:', error);
      setAlert({ 
        severity: 'error', 
        message: error.response?.data?.message || 'Sync failed' 
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoSyncSetup = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${base_url}/v1/stock/sync/auto-sync`, autoSyncSettings, { headers });
      setAutoSyncDialogOpen(false);
      setAlert({ 
        severity: 'success', 
        message: autoSyncSettings.enabled ? 'Auto-sync enabled' : 'Auto-sync disabled' 
      });
      await fetchSyncStatus();
    } catch (error) {
      console.error('Error setting up auto-sync:', error);
      setAlert({ 
        severity: 'error', 
        message: error.response?.data?.message || 'Failed to setup auto-sync' 
      });
    }
  };

  const handleImport = async () => {
    try {
      setSyncing(true);
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${base_url}/v1/stock/sync/import`, importSettings, { headers });
      setImportDialogOpen(false);
      setAlert({ 
        severity: 'success', 
        message: `Import completed: ${response.data.data.success.length} items imported` 
      });
      await fetchSyncStatus();
    } catch (error) {
      console.error('Error during import:', error);
      setAlert({ 
        severity: 'error', 
        message: error.response?.data?.message || 'Import failed' 
      });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const getConnectionStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'disconnected': return 'error';
      case 'local': return 'info';
      default: return 'default';
    }
  };

  const getConnectionStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckCircle />;
      case 'disconnected': return <Error />;
      case 'local': return <Settings />;
      default: return <Warning />;
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Sync Management
      </Typography>

      {/* Alert */}
      {alert && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Current Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Data Source
              </Typography>
              
              {syncStatus?.activeDataSource && (
                <>
                  <Box display="flex" alignItems="center" mb={2}>
                    <CloudSync sx={{ mr: 1 }} />
                    <Typography variant="h5">
                      {syncStatus.activeDataSource.name}
                    </Typography>
                  </Box>
                  
                  <Chip 
                    icon={getConnectionStatusIcon(syncStatus.connectionStatus)}
                    label={syncStatus.connectionStatus?.toUpperCase() || 'Unknown'}
                    color={getConnectionStatusColor(syncStatus.connectionStatus)}
                    sx={{ mb: 2 }}
                  />
                  
                  {syncStatus.activeDataSource.lastSync && (
                    <Typography variant="body2" color="text.secondary">
                      Last Sync: {new Date(syncStatus.activeDataSource.lastSync).toLocaleString()}
                    </Typography>
                  )}
                  
                  <Box display="flex" alignItems="center" mt={2}>
                    <Schedule sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      Auto-sync: {syncStatus.activeDataSource.autoSync ? 
                        `Enabled (${syncStatus.activeDataSource.syncInterval} min)` : 
                        'Disabled'}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sync Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sync Statistics
              </Typography>
              
              {syncStatus?.syncStats && (
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="h4" color="primary">
                      {syncStatus.syncStats.productsWithOdooId}
                    </Typography>
                    <Typography variant="caption">
                      /{syncStatus.syncStats.totalProducts} Products
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4" color="secondary">
                      {syncStatus.syncStats.ownersWithOdooId}
                    </Typography>
                    <Typography variant="caption">
                      /{syncStatus.syncStats.totalOwners} Owners
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4" color="success.main">
                      {syncStatus.syncStats.movesWithOdooId}
                    </Typography>
                    <Typography variant="caption">
                      /{syncStatus.syncStats.totalMoves} Moves
                    </Typography>
                  </Grid>
                </Grid>
              )}
              
              {syncStatus?.activeSyncJobs && syncStatus.activeSyncJobs.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color="warning.main">
                    Active sync jobs: {syncStatus.activeSyncJobs.length}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sync Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sync Actions
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={syncing ? <CircularProgress size={20} /> : <Sync />}
                    onClick={() => handleManualSync()}
                    disabled={syncing || syncStatus?.connectionStatus !== 'connected'}
                    size="large"
                  >
                    {syncing ? 'Syncing...' : 'Full Sync'}
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Settings />}
                    onClick={() => setAutoSyncDialogOpen(true)}
                    size="large"
                  >
                    Auto-sync Setup
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GetApp />}
                    onClick={() => setImportDialogOpen(true)}
                    disabled={syncStatus?.connectionStatus !== 'connected'}
                    size="large"
                  >
                    Import from Odoo
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Assessment />}
                    onClick={fetchSyncStatus}
                    size="large"
                  >
                    Refresh Status
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sync Results */}
        {syncResults && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Last Sync Results
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={3}>
                    <Typography variant="h4" color="success.main">
                      {syncResults.summary.totalSuccessful}
                    </Typography>
                    <Typography variant="body2">Successful</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h4" color="error.main">
                      {syncResults.summary.totalFailed}
                    </Typography>
                    <Typography variant="body2">Failed</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h4" color="primary">
                      {syncResults.summary.totalProcessed}
                    </Typography>
                    <Typography variant="body2">Total</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h4" color="text.secondary">
                      {Math.round(syncResults.summary.duration / 1000)}s
                    </Typography>
                    <Typography variant="body2">Duration</Typography>
                  </Grid>
                </Grid>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>Detailed Results</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          Products ({syncResults.products.success.length + syncResults.products.failed.length})
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={(syncResults.products.success.length / (syncResults.products.success.length + syncResults.products.failed.length)) * 100}
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2">
                          Success: {syncResults.products.success.length}, Failed: {syncResults.products.failed.length}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          Owners ({syncResults.owners.success.length + syncResults.owners.failed.length})
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={(syncResults.owners.success.length / (syncResults.owners.success.length + syncResults.owners.failed.length)) * 100}
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2">
                          Success: {syncResults.owners.success.length}, Failed: {syncResults.owners.failed.length}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          Moves ({syncResults.moves.success.length + syncResults.moves.failed.length})
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={(syncResults.moves.success.length / (syncResults.moves.success.length + syncResults.moves.failed.length)) * 100}
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2">
                          Success: {syncResults.moves.success.length}, Failed: {syncResults.moves.failed.length}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {/* Failed Items */}
                    {(syncResults.products.failed.length > 0 || syncResults.owners.failed.length > 0 || syncResults.moves.failed.length > 0) && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" color="error" gutterBottom>
                          Failed Items:
                        </Typography>
                        <List dense>
                          {syncResults.products.failed.map((item, index) => (
                            <ListItem key={`product-${index}`}>
                              <ListItemIcon>
                                <Error color="error" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`Product: ${item.name}`}
                                secondary={item.error}
                              />
                            </ListItem>
                          ))}
                          {syncResults.owners.failed.map((item, index) => (
                            <ListItem key={`owner-${index}`}>
                              <ListItemIcon>
                                <Error color="error" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`Owner: ${item.name}`}
                                secondary={item.error}
                              />
                            </ListItem>
                          ))}
                          {syncResults.moves.failed.map((item, index) => (
                            <ListItem key={`move-${index}`}>
                              <ListItemIcon>
                                <Error color="error" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`Move: ${item.moveId || 'Unknown'}`}
                                secondary={item.error}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Auto-sync Setup Dialog */}
      <Dialog open={autoSyncDialogOpen} onClose={() => setAutoSyncDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto-sync Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoSyncSettings.enabled}
                  onChange={(e) => setAutoSyncSettings({ ...autoSyncSettings, enabled: e.target.checked })}
                />
              }
              label="Enable Auto-sync"
            />
            
            {autoSyncSettings.enabled && (
              <TextField
                fullWidth
                type="number"
                label="Sync Interval (minutes)"
                value={autoSyncSettings.intervalMinutes}
                onChange={(e) => setAutoSyncSettings({ 
                  ...autoSyncSettings, 
                  intervalMinutes: parseInt(e.target.value) 
                })}
                sx={{ mt: 2 }}
                inputProps={{ min: 1, max: 1440 }}
                helperText="Minimum 1 minute, maximum 24 hours (1440 minutes)"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoSyncDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAutoSyncSetup} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import from Odoo</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              select
              label="Entity Type"
              value={importSettings.entityType}
              onChange={(e) => setImportSettings({ ...importSettings, entityType: e.target.value })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="products">Products</MenuItem>
              <MenuItem value="partners">Partners (Stock Owners)</MenuItem>
              <MenuItem value="stock_moves">Stock Moves</MenuItem>
            </TextField>
            
            <Typography variant="body2" color="text.secondary">
              This will import data from Odoo and create/update corresponding records in the local database.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleImport} variant="contained">Import</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SyncManagement;